import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader, RefreshCw, AlertCircle, Wine, Tag, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';

import { useFooterData } from '../contexts/FooterContext';
import { API } from '../utils/api';
import { showSingleToast } from '../utils/helpers';
import { generateListPageJsonLd } from '../utils/jsonLd';
import { generateSEOIntro } from '../utils/seoContent';

import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import StatsCard from '../components/StatsCard';
import SearchAndFilter from '../components/SearchAndFilter';
import MobileChatButton from '../components/MobileChatButton';
import WineDealCard from '../components/WineDealCard';
import StaticContentSection from '../components/StaticContentSection';

// Main Home Component
const Home = () => {
  const { refreshFooterData } = useFooterData();
  const [wineDeals, setWineDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [totalDeals, setTotalDeals] = useState(0);
  const [seoData, setSeoData] = useState(null);

  // Use refs to avoid closure issues
  const currentOffsetRef = useRef(0);
  const isSearchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);

  // Fetch SEO data for homepage
  useEffect(() => {
    const fetchSeoData = async () => {
      try {
        const response = await axios.get(`${API}/seo/homepage`);
        if (response.data.success && response.data.data.length > 0) {
          setSeoData(response.data.data[0]);
        }
      } catch (error) {
        console.log('SEO data not available');
      }
    };
    fetchSeoData();
  }, []);

  // Update refs when state changes
  useEffect(() => {
    currentOffsetRef.current = currentOffset;
  }, [currentOffset]);

  useEffect(() => {
    isSearchingRef.current = isSearching;
  }, [isSearching]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  const fetchWineDeals = async (forceRefresh = false, offset = 0, append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      
      const response = await axios.get(`${API}/wine-deals?force_refresh=${forceRefresh}&limit=40&offset=${offset}&fields=card`);
      if (response.data.success) {
        const newDeals = response.data.data;
        
        if (append) {
          setWineDeals(prev => {
            const existingIds = new Set(prev.map(deal => deal.id));
            const uniqueNewDeals = newDeals.filter(deal => !existingIds.has(deal.id));
            return [...prev, ...uniqueNewDeals];
          });
        } else {
          setWineDeals(newDeals);
        }
        
        setLastUpdated(response.data.last_updated);
        
        if (response.data.total) {
          setTotalDeals(response.data.total);
        }
        
        setHasMore(newDeals.length === 40);
      }
    } catch (error) {
      console.error('Error fetching wine deals:', error);
      showSingleToast('Failed to load wine deals', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = async (append = false, offset = 0) => {
    try {
      if (!append) {
        setLoading(true);
        setIsSearching(true);
        setCurrentOffset(0);
      } else {
        setLoadingMore(true);
      }
      
      const searchRequest = {
        query: searchQuery.trim() || null,
        filters: null,
        sort_by: "deal_score",
        sort_order: "desc",
        limit: 40,
        offset: offset
      };

      const response = await axios.post(`${API}/wine-deals/search?fields=card`, searchRequest);
      if (response.data.success) {
        const newDeals = response.data.data;
        
        if (append) {
          setWineDeals(prev => {
            const existingIds = new Set(prev.map(deal => deal.id));
            const uniqueNewDeals = newDeals.filter(deal => !existingIds.has(deal.id));
            return [...prev, ...uniqueNewDeals];
          });
        } else {
          setWineDeals(newDeals);
        }
        
        setHasMore(response.data.pagination?.has_more || false);
      }
    } catch (error) {
      console.error('Error searching wine deals:', error);
      showSingleToast('Search failed', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreDeals = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    
    const nextOffset = currentOffsetRef.current + 40;
    setCurrentOffset(nextOffset);
    currentOffsetRef.current = nextOffset;
    
    try {
      if (isSearchingRef.current) {
        await handleSearch(true, nextOffset);
      } else {
        await fetchWineDeals(false, nextOffset, true);
      }
    } catch (error) {
      console.error('Error loading more deals:', error);
      setCurrentOffset(currentOffsetRef.current - 40);
      currentOffsetRef.current = currentOffsetRef.current - 40;
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/wine-deals/stats/summary`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const resetToAllDeals = () => {
    setIsSearching(false);
    setCurrentOffset(0);
    setSearchQuery('');
    fetchWineDeals();
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      
      const syncPromises = [
        axios.post(`${API}/wine-deals/sync`),
        axios.post(`${API}/seo/sync`),
        axios.post(`${API}/static-content/sync`)
      ];
      
      await Promise.all(syncPromises);
      
      setTimeout(() => {
        resetToAllDeals();
        refreshFooterData();
        setSyncing(false);
      }, 3000);
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchWineDeals();
    fetchStats();
  }, []);

  return (
    <div className="min-h-screen" style={{backgroundColor: '#E9D8D5'}}>
      <SEOHead 
        title={seoData?.meta_title || "Great Wine Deals - Top Rated Wines Under $30"}
        description={seoData?.meta_description || "Discover exceptional wine deals under $30 with professional ratings. Browse by country, variety, region, and wine type. Daily updated deals from around the world."}
        canonicalUrl={`${window.location.origin}/`}
        jsonLd={generateListPageJsonLd(
          "Top Rated Wine Deals Under $30",
          "Discover exceptional wine deals under $30 with professional ratings.",
          wineDeals,
          `${window.location.origin}/`
        )}
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">🍷</span>
              <div>
                <div className="text-2xl font-bold text-gray-900">Great Wine Deals</div>
                <p className="text-sm text-gray-600">
                  {lastUpdated && `Last updated: ${new Date(lastUpdated).toLocaleString()}`}
                </p>
              </div>
            </div>
            
            <Button 
              onClick={handleSync}
              disabled={syncing}
              style={{backgroundColor: '#5D0F0F'}}
              className="hover:opacity-90 text-white"
            >
              {syncing ? (
                <Loader className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards - Hidden on mobile */}
        {stats && (
          <div className="hidden md:grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatsCard
              title="Total Deals"
              value={stats.total_deals}
              icon={Wine}
              color="indigo"
            />
            <StatsCard
              title="Average Price"
              value={`$${stats.avg_price.toFixed(0)}`}
              icon={Tag}
              color="green"
            />
            <StatsCard
              title="Lowest Price"
              value={`$${Math.round(stats.price_range.min)}`}
              icon={Calendar}
              color="blue"
            />
            <StatsCard
              title="Varieties"
              value={stats.varieties ? Object.keys(stats.varieties).length : 0}
              emoji="🍇"
              color="purple"
            />
          </div>
        )}

        {/* Search */}
        <SearchAndFilter
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onSearch={handleSearch}
        />

        {/* Mobile Chat Button - Under Search */}
        <div className="md:hidden mt-4">
          <MobileChatButton />
        </div>

        {/* SEO Intro - Uses H1 keywords naturally */}
        {seoData?.h1 && !loading && wineDeals.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{seoData.h1}</h1>
            <p className="text-gray-700 leading-relaxed">
              {seoData.p || generateSEOIntro(seoData.h1, 'home', { dealCount: wineDeals.length })}
            </p>
          </div>
        )}

        {/* Wine Deals Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin" style={{color: '#5D0F0F'}} />
            <span className="ml-3 text-lg" style={{color: '#292021'}}>Loading wine deals...</span>
          </div>
        ) : wineDeals.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No wine deals found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search criteria or sync with Google Sheets.</p>
            {isSearching && (
              <Button onClick={resetToAllDeals} style={{backgroundColor: '#5D0F0F'}} className="hover:opacity-90 text-white">
                Show All Deals
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {wineDeals.map((deal, index) => (
                <WineDealCard key={deal.id} deal={deal} position={index + 1} />
              ))}
            </div>
            
            {/* Load More Button */}
            {hasMore && !loadingMore && (
              <div className="flex justify-center py-8">
                <Button
                  onClick={loadMoreDeals}
                  style={{backgroundColor: '#5D0F0F'}}
                  className="hover:opacity-90 text-white px-8 py-3"
                  data-testid="load-more-btn"
                >
                  Load More Wines
                </Button>
              </div>
            )}
            
            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin" style={{color: '#5D0F0F'}} />
                <span className="ml-3" style={{color: '#292021'}}>Loading more deals...</span>
              </div>
            )}
            
            {/* End of Results */}
            {!hasMore && !loadingMore && wineDeals.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-600">You've seen all {wineDeals.length} wine deals!</p>
                {isSearching && (
                  <Button 
                    variant="link" 
                    onClick={resetToAllDeals}
                    className="mt-2"
                    style={{color: '#5D0F0F'}}
                  >
                    Show All Deals
                  </Button>
                )}
              </div>
            )}
          </>
        )}
        
        {/* Static Content Section - hide SEO block as it's shown in intro above */}
        <div className="mt-12">
          <StaticContentSection page="homepage" setSEO={false} hideSEOBlock={true} />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Home;
